import React, { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Field, Input, Empty, Badge } from "@/components/ui-kit";
import { Trophy, MapPin, Phone, Mail, Calendar, User, Save, BellRing, History, Camera, HeartHandshake, KeyRound, Copy, RefreshCw, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import AccessCodes from "@/components/AccessCodes";
import { Link } from "react-router-dom";

export default function ClubInfo() {
  const { user, can } = useAuth();
  const isAdmin = can("club_edit");
  const canSee = ["admin", "coach"].includes(user?.role);
  const [club, setClub] = useState(null);
  const [edit, setEdit] = useState(false);
  const [autoReminders, setAutoReminders] = useState(true);
  const [regOpen, setRegOpen] = useState(true);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [feeTypes, setFeeTypes] = useState([]);
  const [savingFees, setSavingFees] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [apiKey, setApiKey] = useState("");
  const [apiRotated, setApiRotated] = useState(null);
  const [apiAccess, setApiAccess] = useState({ at: null, ip: null, count: 0 });
  const [apiRevealed, setApiRevealed] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const loadApiKey = () => api.get("/admin/affilies-api-key").then((r) => { setApiKey(r.data.api_key); setApiRotated(r.data.rotated_at); setApiAccess({ at: r.data.last_access_at, ip: r.data.last_access_ip, count: r.data.access_count || 0 }); }).catch(() => {});
  const regenApiKey = async () => {
    if (!window.confirm("Régénérer la clé API ? L'ancienne clé sera immédiatement révoquée et cessera de fonctionner.")) return;
    setRegenerating(true);
    try { const { data } = await api.post("/admin/affilies-api-key/regenerate"); setApiKey(data.api_key); setApiRotated(data.rotated_at); setApiAccess({ at: null, ip: null, count: 0 }); setApiRevealed(true); toast.success("Nouvelle clé API générée"); }
    catch { toast.error("Erreur"); }
    finally { setRegenerating(false); }
  };
  const exportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/export/affilies.json`;
  const copyText = (t, label) => { navigator.clipboard.writeText(t).then(() => toast.success(`${label} copié`)).catch(() => toast.error("Copie impossible")); };

  const load = () => api.get("/club").then((r) => { setClub(r.data); setForm(r.data); });
  useEffect(() => {
    load();
    api.get("/settings").then((r) => { setAutoReminders(r.data.auto_reminders); setRegOpen(r.data.registration_open); }).catch(() => {});
    api.get("/reminders/log").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/settings/fees").then((r) => setFeeTypes(r.data.fee_types)).catch(() => {});
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {});
    if (isAdmin) loadApiKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveFees = async () => {
    setSavingFees(true);
    try {
      const { data } = await api.put("/settings/fees", { fee_types: feeTypes });
      setFeeTypes(data.fee_types);
      toast.success("Montants des cotisations mis à jour");
    } catch { toast.error("Erreur"); }
    finally { setSavingFees(false); }
  };

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/club/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo du club mis à jour");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Échec de l'upload"); }
    finally { setUploadingLogo(false); }
  };

  const toggleAuto = async () => {
    const next = !autoReminders;
    setAutoReminders(next);
    try { await api.put("/settings", { auto_reminders: next }); toast.success(next ? "Rappels automatiques activés" : "Rappels automatiques désactivés"); }
    catch { setAutoReminders(!next); toast.error("Erreur"); }
  };

  const toggleReg = async () => {
    const next = !regOpen;
    setRegOpen(next);
    try { await api.put("/settings", { registration_open: next }); toast.success(next ? "Inscriptions libres ouvertes" : "Inscriptions libres fermées (code requis)"); }
    catch { setRegOpen(!next); toast.error("Erreur"); }
  };

  const save = async (e) => {
    e.preventDefault();
    await api.put("/club", form); toast.success("Informations mises à jour"); setEdit(false); load();
  };

  if (!club) return null;

  if (edit) {
    return (
      <div>
        <PageHeader title="Modifier le club" />
        <Card className="max-w-xl">
          <form onSubmit={save}>
            <Field label="Nom du club"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Description"><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fondé en"><Input value={form.founded || ""} onChange={(e) => setForm({ ...form, founded: e.target.value })} /></Field>
              <Field label="Président"><Input value={form.president || ""} onChange={(e) => setForm({ ...form, president: e.target.value })} /></Field>
            </div>
            <Field label="Adresse"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Téléphone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
              <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            </div>
            <div className="flex gap-2 mt-2">
              <Button data-testid="save-club-btn" className="flex-1"><Save size={16} /> Enregistrer</Button>
              <Button type="button" variant="outline" onClick={() => setEdit(false)}>Annuler</Button>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  const info = [
    { icon: Calendar, label: "Fondé en", value: club.founded },
    { icon: User, label: "Président", value: club.president },
    { icon: MapPin, label: "Adresse", value: club.address },
    { icon: Phone, label: "Téléphone", value: club.phone },
    { icon: Mail, label: "Email", value: club.email },
  ];

  return (
    <div>
      <PageHeader title="Infos Club" subtitle="Présentation et coordonnées du club."
        action={isAdmin && <Button data-testid="edit-club-btn" variant="outline" onClick={() => setEdit(true)}>Modifier</Button>} />

      <Card className="mb-6 overflow-hidden relative">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://images.pexels.com/photos/10347865/pexels-photo-10347865.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative flex items-center gap-4">
          {club.logo_path ? (
            <img src={fileUrl(club.logo_path)} alt="logo" data-testid="club-logo" className="w-16 h-16 rounded-xl object-cover bg-white border border-border" />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-primary flex items-center justify-center"><Trophy className="text-primary-foreground" size={32} /></div>
          )}
          <div className="flex-1">
            <h2 className="font-display text-3xl font-black uppercase tracking-tighter">{club.name}</h2>
            <p className="text-muted-foreground text-sm mt-1">{club.description}</p>
          </div>
          {isAdmin && (
            <label className="cursor-pointer shrink-0">
              <span className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-secondary text-sm font-bold hover:bg-secondary/70 transition-colors">
                <Camera size={15} /> {uploadingLogo ? "Envoi..." : "Logo"}
              </span>
              <input data-testid="club-logo-input" type="file" accept="image/*" className="hidden" onChange={onLogoChange} disabled={uploadingLogo} />
            </label>
          )}
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4">
        {info.filter((i) => i.value).map((i, idx) => (
          <Card key={idx} className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-lg bg-primary/15 flex items-center justify-center text-primary"><i.icon size={20} /></div>
            <div><p className="text-xs uppercase tracking-widest text-muted-foreground">{i.label}</p><p className="font-semibold">{i.value}</p></div>
          </Card>
        ))}
      </div>

      {sponsors.length > 0 && (
        <div className="mt-10" data-testid="club-sponsors-band">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold uppercase tracking-tight flex items-center gap-2"><HeartHandshake size={20} className="text-primary" /> Nos partenaires</h2>
            <Link to="/sponsors" className="text-sm font-bold text-primary hover:underline">Tout voir →</Link>
          </div>
          <Card>
            <div className="flex flex-wrap items-center gap-4">
              {sponsors.map((s) => {
                const inner = s.logo
                  ? <img src={fileUrl(s.logo)} alt={s.name} title={s.name} className="max-h-14 max-w-[140px] object-contain" />
                  : <span className="text-sm font-bold text-foreground/70 px-3">{s.name}</span>;
                return (
                  <div key={s.id} data-testid="club-sponsor-logo" className="h-16 min-w-[120px] px-3 rounded-lg bg-white flex items-center justify-center">
                    {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer">{inner}</a> : inner}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {isAdmin && (
        <>
          <h2 className="font-display text-xl font-bold uppercase tracking-tight mt-10 mb-4 flex items-center gap-2">
            <Save size={20} className="text-primary" /> Montants des cotisations (par saison)
          </h2>
          <Card className="mb-4">
            <div className="space-y-3">
              {feeTypes.map((f, i) => (
                <div key={f.key} data-testid={`fee-type-${f.key}`} className="flex items-center gap-3">
                  <span className="flex-1 font-semibold text-sm">{f.label}</span>
                  <div className="w-32">
                    <Input data-testid={`fee-amount-${f.key}`} type="number" step="0.01" value={f.amount}
                      onChange={(e) => setFeeTypes((ts) => ts.map((t, j) => j === i ? { ...t, amount: parseFloat(e.target.value) || 0 } : t))} />
                  </div>
                  <span className="text-muted-foreground text-sm">€</span>
                </div>
              ))}
            </div>
            <Button data-testid="save-fees-btn" onClick={saveFees} disabled={savingFees} className="mt-4">
              <Save size={16} /> {savingFees ? "Enregistrement..." : "Enregistrer les montants"}
            </Button>
          </Card>
        </>
      )}

      {isAdmin && <AccessCodes registrationOpen={regOpen} onToggleRegistration={toggleReg} />}

      {isAdmin && (
        <Card className="mt-6 p-6" data-testid="api-key-card">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center text-primary"><KeyRound size={18} /></span>
            <div>
              <h3 className="font-display text-lg font-bold uppercase tracking-wide">Clé API — Export des affiliés</h3>
              <p className="text-xs text-muted-foreground">Permet à un système externe de récupérer la liste des affiliés (JSON) de façon sécurisée.</p>
            </div>
          </div>
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Clé API</label>
              <div className="flex items-center gap-2 flex-wrap">
                <code data-testid="api-key-value" className="flex-1 min-w-0 break-all rounded-lg bg-secondary/60 px-3 py-2 text-sm font-mono">
                  {apiRevealed ? apiKey : (apiKey ? "•".repeat(Math.min(apiKey.length, 40)) : "—")}
                </code>
                <Button data-testid="api-key-reveal" variant="outline" className="h-9 px-3" onClick={() => setApiRevealed((v) => !v)}>
                  {apiRevealed ? <EyeOff size={15} /> : <Eye size={15} />}
                </Button>
                <Button data-testid="api-key-copy" variant="outline" className="h-9 px-3" onClick={() => copyText(apiKey, "Clé API")}><Copy size={15} /> Copier</Button>
                <Button data-testid="api-key-regen" variant="outline" className="h-9 px-3" disabled={regenerating} onClick={regenApiKey}><RefreshCw size={15} /> {regenerating ? "..." : "Régénérer"}</Button>
              </div>
              {apiRotated && <p className="text-[11px] text-muted-foreground mt-1.5">Dernière génération : {new Date(apiRotated).toLocaleString("fr-FR")}</p>}
              <p data-testid="api-key-last-access" className="text-[11px] text-muted-foreground mt-0.5">
                {apiAccess.at
                  ? `Dernier accès : ${new Date(apiAccess.at).toLocaleString("fr-FR")} · IP ${apiAccess.ip || "?"} · ${apiAccess.count} appel${apiAccess.count > 1 ? "s" : ""}`
                  : "Aucun accès à l'export pour l'instant."}
              </p>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">URL de l'export</label>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="flex-1 min-w-0 break-all rounded-lg bg-secondary/60 px-3 py-2 text-xs font-mono" data-testid="api-export-url">{exportUrl}</code>
                <Button variant="outline" className="h-9 px-3" onClick={() => copyText(exportUrl, "URL")}><Copy size={15} /></Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Ajoutez l'en-tête <code className="font-mono">X-API-Key: &lt;clé&gt;</code> (ou <code className="font-mono">?api_key=&lt;clé&gt;</code>). Champs renvoyés : nom, prénom, date de naissance, groupe, catégorie, statut.</p>
              <p className="text-[11px] text-muted-foreground mt-1">Filtres optionnels : <code className="font-mono">?groupe=gr1</code>, <code className="font-mono">?categorie=2016</code>, <code className="font-mono">?statut=affilié</code> (ou archivé / inactif) — combinables.</p>
            </div>
            <p className="text-[11px] text-orange-500 flex items-center gap-1.5"><KeyRound size={12} /> Traitez cette clé comme un mot de passe. La régénération révoque immédiatement l'ancienne clé.</p>
          </div>
        </Card>
      )}

      {canSee && (
        <>
          <h2 className="font-display text-xl font-bold uppercase tracking-tight mt-10 mb-4 flex items-center gap-2">
            <BellRing size={20} className="text-primary" /> Rappels automatiques
          </h2>
          <Card className="flex items-center justify-between">
            <div>
              <p className="font-semibold">Envoi automatique par email</p>
              <p className="text-xs text-muted-foreground mt-1">Rappels de cotisation (hebdo) et convocations aux événements (J-3).</p>
            </div>
            <button data-testid="toggle-auto-reminders" onClick={isAdmin ? toggleAuto : undefined} disabled={!isAdmin}
              className={`relative w-14 h-8 rounded-full transition-colors ${autoReminders ? "bg-primary" : "bg-secondary"} ${!isAdmin ? "opacity-60 cursor-not-allowed" : ""}`}>
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${autoReminders ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </Card>

          <h2 className="font-display text-xl font-bold uppercase tracking-tight mt-10 mb-4 flex items-center gap-2">
            <History size={20} className="text-primary" /> Historique des rappels
          </h2>
          {logs.length ? (
            <div className="space-y-2">
              {logs.map((l) => (
                <Card key={l.id} data-testid="reminder-log-row" className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <Badge color={l.type === "cotisation" ? "yellow" : l.type === "cancellation" ? "red" : "blue"}>{l.type === "cotisation" ? "Cotisation" : l.type === "cancellation" ? "Annulation" : "Convocation"}</Badge>
                    <div>
                      <p className="font-semibold text-sm">{l.member_name}</p>
                      {l.event_title && <p className="text-xs text-muted-foreground">{l.event_title}</p>}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{l.sent_at?.slice(0, 16).replace("T", " ")}</span>
                </Card>
              ))}
            </div>
          ) : <Empty text="Aucun rappel envoyé pour l'instant." />}
        </>
      )}
    </div>
  );
}
